/* eslint-disable max-len */
import { graphql as coreGraphqlTypes } from '@sitebuilder/core';
import * as qglQuery from './graphqlQuery';
import * as types from './types';
import * as viewModelTypes from './viewModelTypes';

export { types, viewModelTypes, qglQuery };

const buildQuery = () => {
    const { query, transform } = qglQuery;
    return { query, transform };
};

function transformPortfolioTypeToHoldingsViewModel(
    portfolio: types.HoldingsPortfolio,
    flags: types.HoldingsIncludeFlags
): viewModelTypes.HoldingsViewModel {
    const holdings: viewModelTypes.HoldingsViewModel = {
        effectiveDate: portfolio.effectiveDate,
        holdingsContexts: [],
    };

    if (typeof portfolio.preferredShareAllocations !== 'undefined')
        holdings.preferredShareAllocations = portfolio.preferredShareAllocations;

    if (typeof portfolio.underlyingFundCompanyBreakdowns !== 'undefined')
        holdings.underlyingFundCompanyBreakdowns = portfolio.underlyingFundCompanyBreakdowns;

    if (typeof portfolio.underlyingFund !== 'undefined')
        holdings.underlyingFund = portfolio.underlyingFund;

    if (typeof portfolio.numberOfBondPositions !== 'undefined')
        holdings.numberOfBondPositions = portfolio.numberOfBondPositions;

    if (typeof portfolio.numberOfEquityFunds !== 'undefined')
        holdings.numberOfEquityFunds = portfolio.numberOfEquityFunds;

    if (typeof portfolio.cashEquivalents !== 'undefined')
        holdings.cashEquivalents = portfolio.cashEquivalents;

    if (typeof portfolio.numberOfEquityPositions !== 'undefined')
        holdings.numberOfEquityPositions = portfolio.numberOfEquityPositions;

    if (typeof portfolio.numberOfFixedIncomeFunds !== 'undefined')
        holdings.numberOfFixedIncomeFunds = portfolio.numberOfFixedIncomeFunds;

    if (typeof portfolio.numberOfHoldings !== 'undefined')
        holdings.numberOfHoldings = portfolio.numberOfHoldings;

    if (typeof portfolio.numberOfOtherPositions !== 'undefined')
        holdings.numberOfOtherPositions = portfolio.numberOfOtherPositions;

    if (typeof portfolio.numberOfPositions !== 'undefined')
        holdings.numberOfPositions = portfolio.numberOfPositions;

    if (typeof portfolio.numberOfCashPositions !== 'undefined')
        holdings.numberOfCashPositions = portfolio.numberOfCashPositions;

    if (typeof portfolio.numberOfSpecialtyFunds !== 'undefined')
        holdings.numberOfSpecialtyFunds = portfolio.numberOfSpecialtyFunds;

    if (typeof portfolio.averageCoupon !== 'undefined')
        holdings.averageCoupon = portfolio.averageCoupon;

    if (typeof portfolio.averageDividendYield !== 'undefined')
        holdings.averageDividendYield = portfolio.averageDividendYield;

    if (typeof portfolio.priceEarnings !== 'undefined')
        holdings.priceEarnings = portfolio.priceEarnings;

    if (typeof portfolio.priceBook !== 'undefined') holdings.priceBook = portfolio.priceBook;

    if (typeof portfolio.numberOfTotalFunds !== 'undefined')
        holdings.numberOfTotalFunds = portfolio.numberOfTotalFunds;

    if (typeof portfolio.numberOfDeals !== 'undefined')
        holdings.numberOfDeals = portfolio.numberOfDeals;

    if (typeof portfolio.numberOfSpacs !== 'undefined')
        holdings.numberOfSpacs = portfolio.numberOfSpacs;

    if (typeof portfolio.averageMarketCap !== 'undefined')
        holdings.averageMarketCap = portfolio.averageMarketCap;

    if (typeof portfolio.averageMarketCapClientSource !== 'undefined')
        holdings.averageMarketCapClientSource = portfolio.averageMarketCapClientSource;

    if (typeof portfolio.underlyingFund !== 'undefined')
        holdings.underlyingFund = portfolio.underlyingFund;

    if (typeof portfolio.topHoldingsType !== 'undefined')
        holdings.topHoldingsType = portfolio.topHoldingsType;

    if (typeof portfolio.yieldToMaturity !== 'undefined')
        holdings.yieldToMaturity = portfolio.yieldToMaturity;

    if (typeof portfolio.modifiedDuration !== 'undefined')
        holdings.modifiedDuration = portfolio.modifiedDuration;

    if (typeof portfolio.averageTerm !== 'undefined') holdings.averageTerm = portfolio.averageTerm;

    if (typeof portfolio.averageCreditQuality !== 'undefined')
        holdings.averageCreditQuality = portfolio.averageCreditQuality;

    if (typeof portfolio.top10EquityTotal !== 'undefined')
        holdings.top10EquityTotal = portfolio.top10EquityTotal;

    if (typeof portfolio.top10BondTotal !== 'undefined')
        holdings.top10BondTotal = portfolio.top10BondTotal;

    if (typeof flags.top10HoldingContext === 'string') {
        holdings.top10HoldingContext = flags.top10HoldingContext;
    }

    const mapUnderlyingHoldingsContexts = (h: types.TopHoldingAllocation) => ({
        underlyingHoldings:
            h.top5UnderlyingFundHoldings && h.top5UnderlyingFundHoldings.length > 0
                ? ([
                      {
                          dimension: 'top5UnderlyingFundHoldings',
                          allocations: h.top5UnderlyingFundHoldings?.map(
                              (a) =>
                                  ({
                                      holding: a.holding,
                                      allocation: a.allocation,
                                      allocationShort: a.allocationShort,
                                      allocationLong: a.allocationLong,
                                      effectiveDate: holdings.effectiveDate,
                                      allocationGross: a.allocationGross,
                                      benchmarkAllocation: a.benchmarkAllocation,
                                  }) as viewModelTypes.HoldingsContextAllocationViewModel
                          ),
                      },
                  ] as viewModelTypes.HoldingsContextViewModel[])
                : undefined,
    });

    const holdingsContextsList = {
        preferredShareAllocations: {
            flag: 'showPreferredShareAllocation',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        underlyingFundCompanyBreakdowns: {
            flag: 'showUnderlyingFundCompanyBreakdowns',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        underlyingFundHoldings: {
            flag: 'showUnderlyingFundHolding',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        canadianEquityBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        fixedIncomeBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        foreignEquityBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        specialtyBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        specialtyCategoryBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        top10Holdings: {
            flag: 'showTop10Holding',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        topHoldingsMixed: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        topHoldingsPlusCash: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        top10HoldingsOfLargestFundHolding: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        topBondHoldings: { flag: null, holdingValue: (h: types.HoldingAllocation) => h.holding },
        topEquityHoldings: { flag: null, holdingValue: (h: types.HoldingAllocation) => h.holding },
        globalEquityBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        unitedStatesEquityBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        internationalEquityBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        canadianEquityCategoryBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        canadianEquityPortfolioBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        bondPortfolioBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        globalEquityPortfolioBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
            additionalValues: (h: types.TopHoldingAllocation) => ({
                ...mapUnderlyingHoldingsContexts(h),
            }),
        },
        fixedIncomeCategoryBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        fixedIncomeHoldingsBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        foreignEquityCategoryBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        foreignIncomeHoldingsBreakdown: {
            flag: null,
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        strategyWeightingsAllocations: {
            flag: null,
            holdingValue: (h: types.StrategyWeightingsAllocation) =>
                h.holding || h.strategyWeighting?.name,
            additionalValues: (h: types.StrategyWeightingsAllocation) => ({
                strategyWeighting: h.strategyWeighting,
            }),
        },

        assetMixBreakdowns: {
            flag: null,
            holdingValue: (h: types.AssetMixBreakdown) => h.assetClass,
            additionalValues: (h: types.AssetMixBreakdown) => ({
                assetClass: h.assetClass,
                min: h.min,
                max: h.max,
                strategicBenchmark: h.strategicBenchmark,
                currentTacticalMix: h.currentTacticalMix,
            }),
        },

        assetGroupAllocations: {
            flag: null,
            holdingValue: (h: types.AssetAllocation) => h.holding || h.assetType?.name,
            additionalValues: (h: types.AssetAllocation) => ({ assetType: h.assetType }),
        },
        assetSubGroupAllocations: {
            flag: null,
            holdingValue: (h: types.AssetAllocation) => h.holding || h.assetType?.name,
            additionalValues: (h: types.AssetAllocation) => ({ assetType: h.assetType }),
        },
        assetClassAllocations: {
            flag: null,
            holdingValue: (h: types.AssetAllocation) => h.holding || h.assetType?.name,
            additionalValues: (h: types.AssetAllocation) => ({ assetType: h.assetType }),
        },
        assetAllocations: {
            flag: 'showAssetClassAllocation',
            holdingValue: (h: types.AssetAllocation) => h.holding || h.assetType.name,
            additionalValues: (h: types.AssetAllocation) => ({ assetType: h.assetType }),
        },
        targetAssetAllocations: {
            flag: 'showTargetAssetAllocation',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        bondTermAllocations: {
            flag: 'showBondTermAllocation',
            holdingValue: (h: types.BondTermAllocation) => h.holding || h.bondTerm?.name,
        },
        additionalValues: (h: types.BondTermAllocation) => ({ bondTerm: h.bondTerm }),
        creditQualityRateAllocations: {
            flag: 'showCreditQualityRateAllocation',
            holdingValue: (h: types.CreditQualityRateAllocations) =>
                h.holding || h.creditQualityRating?.name,
            additionalValues: (h: types.CreditQualityRateAllocations) => ({
                creditQualityRating: h.creditQualityRating,
            }),
        },
        currencyAllocations: {
            flag: 'showCurrencyAllocation',
            holdingValue: (h: types.CurrencyAllocation) => h.holding || h.currency?.name,
            additionalValues: (h: types.CurrencyAllocation) => ({ currency: h.currency }),
        },
        fixedIncomeTypeAllocations: {
            flag: 'showFixedIncomeTypeAllocation',
            holdingValue: (h: types.FixedIncomeTypeAllocation) => h.holding || h.assetType?.name,
            additionalValues: (h: types.FixedIncomeTypeAllocation) => ({ assetType: h.assetType }),
        },
        cashTypeAllocations: {
            flag: 'showCashTypeAllocation',
            holdingValue: (h: types.CashTypeAllocation) => h.holding || h.assetType?.name,
            additionalValues: (h: types.CashTypeAllocation) => ({ assetType: h.assetType }),
        },
        geographicAllocations: {
            flag: 'showGeographicAllocation',
            holdingValue: (h: types.GeographicAllocation) => h.holding || h.country?.name,
            additionalValues: (h: types.GeographicAllocation) => ({ country: h.country }),
        },
        marketCapAllocations: {
            flag: 'showMarketCapAllocation',
            holdingValue: (h: types.MarketCapAllocation) => h.holding || h.marketCap?.name,
            additionalValues: (h: types.MarketCapAllocation) => ({ marketCap: h.marketCap }),
        },

        sectorAllocations: {
            flag: 'showSectorAllocation',
            holdingValue: (h: types.SectorAllocation) => h.holding || h.sector?.name,
            additionalValues: (h: types.SectorAllocation) => ({ sector: h.sector }),
        },

        riskAllocations: {
            flag: 'showRiskAllocation',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        securityTypeAllocations: {
            flag: 'showSecurityTypeAllocation',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        exposureAllocations: {
            flag: 'showExposureAllocation',
            holdingValue: (h: types.ExposureAllocation) => h.holding || h.exposureType?.name,
            additionalValues: (h: types.ExposureAllocation) => ({ exposureType: h.exposureType }),
        },
        dollarAllocations: {
            flag: 'showDollarAllocation',
            holdingValue: (h: types.HoldingAllocation) => h.holding,
        },
        globalBondSectorLevel1Breakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        globalBondSectorLevel2Breakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        globalBondSectorLevel3Breakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        globalStockSectorBreakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        morningstarAssetBreakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        morningstarRegionBreakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        morningstarEquityStyleBreakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        morningstarBondStyleBreakdowns: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
        morningstarCreditQualityBreakdown: {
            flag: null,
            holdingValue: (h: types.MorningstarHoldingAllocation) =>
                h.holding || h.typeDescription?.name,
            additionalValues: (h: types.MorningstarHoldingAllocation) => ({
                typeDescription: h.typeDescription,
            }),
        },
    } as Record<
        keyof types.HoldingsPortfolioAllocations,
        {
            flag: keyof types.HoldingsIncludeFlags | null;
            holdingValue: (h: any) => string | undefined;
            additionalValues?: (h: any) => object;
        }
    >;

    const contextNames = Object.keys(
        holdingsContextsList
    ) as (keyof types.HoldingsPortfolioAllocations)[];

    holdings.holdingsContexts = contextNames
        .filter((c) => {
            const flag = holdingsContextsList[c].flag;
            const include = flag !== null ? flags[flag] === true : true;
            return include && portfolio[c];
        })
        .map(
            (c) =>
                ({
                    dimension: c,
                    allocations: portfolio[c]?.map((a) => ({
                        holding: a.holding || holdingsContextsList[c].holdingValue(a),
                        allocation: a.allocation,
                        allocationShort: a.allocationShort,
                        allocationLong: a.allocationLong,
                        effectiveDate: holdings.effectiveDate,
                        allocationGross: a.allocationGross,
                        benchmarkAllocation: a.benchmarkAllocation,
                        ...(holdingsContextsList[c].additionalValues?.call(null, a) || {}),
                    })),
                }) as viewModelTypes.HoldingsContextViewModel
        );

    return holdings;
}

export const transformData = (
    { data }: types.WebProfileResponse,
    locale: string
): viewModelTypes.WebProfileViewModel => {
    if (!data) {
        return {
            entityId: '',
            locale,
            title: '',
            isInactive: false,
            description: '',
            pdfFilename: '',
            overview: {},
            managers: [],
            annotations: [],
            fundamentalMetrics: {},
            benchmark1FundamentalMetrics: {},
            benchmark2FundamentalMetrics: {},
        };
    }

    const overview: viewModelTypes.WebProfileOverviewModel = {};

    const allProfiles = data.allProfiles?.map((p) => {
        const e: viewModelTypes.ProfileListItem = {
            entityId: p.entityId,
            fundName: p.fundName,
            sortOrder: p.sortOrder,
            isInactive: p.isInactive,
            relatedDocuments: p.documents,
        };

        if (Array.isArray(p.webProfileSalesOptions) && p.webProfileSalesOptions.length > 0) {
            e.productGroup = p.webProfileSalesOptions[0].salesOption?.productGroup;
            e.product = p.webProfileSalesOptions[0].salesOption?.product;
            const series = p.webProfileSalesOptions[0].salesOption?.series;

            if (series?.entityId) {
                e.series = {
                    entityId: series?.entityId,
                    seriesType: p.webProfileSalesOptions[0].salesOption?.series?.seriesType,
                };

                const fund = series?.fund;

                if (typeof fund?.entityId === 'string') {
                    e.fund = {
                        entityId: fund?.entityId,
                        shortName: fund?.shortName,
                        fullName: fund?.fullName,
                    };

                    // if sales option does not have product and product group info and fund does,
                    // use the fund values.
                    if (!e.productGroup && fund?.productGroup) {
                        e.productGroup = fund.productGroup;
                    }

                    if (!e.product && fund?.product) {
                        e.product = fund.product;
                    }
                }
            }
        }

        return e;
    });

    const webProfile = (data.webProfiles || [])[0] || {};
    const primaryProfileSalesOption = (webProfile.webProfileSalesOptions || [])[0] || {};
    const primarySalesOption = primaryProfileSalesOption.salesOption || {};
    const primarySeries = primarySalesOption.series || {};
    const fund = primarySeries.fund || {};
    const articlesById = [
        ...(fund.articles || []),
        ...(fund.fundManagers
            ?.map<
                viewModelTypes.Article[]
            >((fm) => [...(fm.manager?.articles1 || []), ...(fm.manager?.articles2 || []), ...(fm.manager?.articles3 || []), ...(fm.manager?.articles4 || [])])
            ?.reduce((memo, obj) => [...memo, ...obj], [] as viewModelTypes.Article[]) || []),
    ].reduce(
        (memo, article) => ({ ...memo, [`${article.entityId || ''}`]: article }),
        {} as Record<string, viewModelTypes.Article>
    );
    const articles = Object.values(articlesById);

    const fees = (primarySeries.fees || [])[0] || {};
    const holdingsPortfolio = (fund.portfolios || [])[0] || {};

    const salesOptionsAnnotations: viewModelTypes.Annotation[] =
        fund?.series
            // Filterout inactive series
            ?.filter((x) => x.isInactive !== true)
            ?.reduce(
                (result, s) =>
                    result?.concat(
                        s?.fundCodes?.reduce(
                            (annotations, fc: any) =>
                                annotations?.concat(fc?.annotations || []) || [],
                            []
                        ) || []
                    ) || [],
                []
            ) || [];

    const annotations: viewModelTypes.Annotation[] = [
        ...(data.globalFundAnnotations || []),
        ...(data.globalWebProfileAnnotations || []),
        ...(data.globalSeriesAnnotations || []),
        ...(webProfile.annotations || []),
        ...(fund.annotations || []),
        ...(primarySeries.annotations || []),
        ...salesOptionsAnnotations,
    ];

    overview.fundName = fund.fullName;
    overview.seriesName = primarySeries.seriesType?.name;
    overview.seriesSortOrder = primarySeries.seriesType?.sortOrder;
    overview.webProfileFundName =
        webProfile.fundName || `${fund.fullName} - ${primarySeries.seriesType?.name}`;

    overview.investmentObjective = fund.investmentObjective;
    overview.investmentStrategy = fund.investmentStrategy;
    overview.investmentPhilosophy = fund.investmentPhilosophy;
    overview.whyInvest = fund.whyInvest;
    overview.whatDoesTheFundInvestIn = fund.whatDoesTheFundInvestIn;
    overview.exchange = fund.exchange;
    overview.exchangeName = fund.exchangeName;

    overview.seriesWhyInvest = primarySeries.whyInvest;
    overview.isin = primarySeries.isin;
    overview.cusip = primarySeries.cusip;
    overview.sedol = primarySeries.sedol;
    overview.ticker = primarySeries.ticker;
    overview.seriesQRCodeUrl = primarySeries.qrCodeUrl;
    overview.isCurrencyHedged = primarySeries.isCurrencyHedged;

    overview.incentiveFee = fund.incentiveFee;
    overview.earlyRedemptionFee = fund.earlyRedemptionFee;

    overview.aum = fund.aum;
    overview.aumDate = fund.aumDate;
    overview.advisor = fund.advisor;
    overview.portfolioManager = fund.portfolioManager;
    overview.subAdvisor = fund.subAdvisor;

    overview.management = fund.management;
    overview.managementDescription = fund.managementDescription;
    overview.managementUrl = fund.managementUrl;

    overview.riskRatingName = fund.riskRating?.name;
    overview.riskRating = fund.riskRating;
    overview.assetClass = fund.cifscCategory?.name;
    overview.assetClassEntityId = fund.assetClass?.entityId;
    overview.assetClassSortOrder = fund.assetClass?.sortOrder;
    overview.fundCategoryName = fund.fundCategory?.name;
    overview.fundCategory = fund.fundCategory;
    overview.morningStarCategoryName = fund.morningStarCategory?.name;
    overview.morningStarCategory = fund.morningStarCategory;
    overview.timeHorizon = fund.timeHorizon?.entityId;

    overview.broker = fund.broker;
    overview.administrator = fund.administrator;
    overview.auditor = fund.auditor;
    overview.lawyer = fund.lawyer;
    overview.customAssetClass = fund.assetClass?.name;
    overview.investmentStyle = fund.investmentStyle?.name;

    overview.fundStatusName = primarySeries.fundStatus?.name;
    overview.fundStatus = primarySeries.fundStatus;
    overview.targetDistribution = primarySeries.targetDistribution;
    overview.performanceFee = primarySeries.performanceFee;
    overview.performanceResetDate = primarySeries.performanceResetDate;
    overview.hurdleRate = primarySeries.hurdleRate;
    overview.marketLiquidity = primarySeries.marketLiquidity;

    overview.inceptionDate = primarySeries.inceptionDate;
    overview.distributions = primarySeries.distributions;
    overview.distributionFrequency = primarySeries.distributionFrequency?.name;
    overview.incomeDistributionFrequency = primarySeries.incomeDistributionFrequency?.name;

    if (Array.isArray(primarySeries.monthEndRecords) && primarySeries.monthEndRecords.length > 0) {
        overview.monthEndRecord = primarySeries.monthEndRecords[0];
    }
    delete primarySeries.monthEndRecords;
    overview.yield12Month = primarySeries.yield12Month;
    overview.fundataAwardWinner = primarySeries.fundataAwardWinner;
    overview.yield7day = primarySeries.yield7day;
    overview.capitalGainsDistributionFrequency =
        primarySeries.capitalGainsDistributionFrequency?.name;
    overview.minimumInvestment = primarySeries.minimumInvestment;

    overview.minimumInitialInvestment = primarySeries.minimumInitialInvestment;
    overview.minimumSubsequentInvestment = primarySeries.minimumSubsequentInvestment;
    overview.minimumPac = primarySeries.minimumPac;
    overview.minimumSubsequentPAC = primarySeries.minimumSubsequentPAC;
    overview.minimumInitialInvestmentRRSP = primarySeries.minimumInitialInvestmentRRSP;
    overview.minimumSubsequentInvestmentRRSP = primarySeries.minimumSubsequentInvestmentRRSP;
    overview.minimumInitialInvestmentAccredited = primarySeries.minimumInitialInvestmentAccredited;
    overview.minimumInitialInvestmentQualified = primarySeries.minimumInitialInvestmentQualified;
    overview.registeredPlanEligibility = primarySeries.registeredPlanEligibility;
    overview.dividendReinvestmentPlanEligibility =
        primarySeries.dividendReinvestmentPlanEligibility;

    overview.rebalancingFrequency = primarySeries.rebalancingFrequency;
    overview.rebalancingThreshold = primarySeries.rebalancingThreshold;

    overview.managementFee = fees.managementFee;
    overview.mer = fees.mer;
    overview.merDate = fees.effectiveDate;
    overview.fees = fees;

    overview.numberOfDeals = holdingsPortfolio.numberOfDeals;
    overview.numberOfSpacs = holdingsPortfolio.numberOfSpacs;
    overview.averageMarketCap = holdingsPortfolio.averageMarketCap?.name;
    overview.currency = primarySeries.currency?.iso4217alpha;

    overview.hasHighWatermark = primarySeries.hasHighWatermark;
    overview.highWatermarkPercent = primarySeries.highWatermarkPercent;
    overview.highWatermarkAmount = primarySeries.highWatermarkAmount;
    overview.highWatermarkDescription = primarySeries.highWatermarkDescription;
    overview.highWatermarkPeriodicalReset = primarySeries.highWatermarkPeriodicalReset;
    overview.highWatermarkResetFrequency = primarySeries.highWatermarkResetFrequency?.name;

    overview.fundCodes = fund.series
        // Filterout inactive series
        ?.filter((x) => x.isInactive !== true)
        .map(
            (s): viewModelTypes.FundSeries => ({
                entityId: s.entityId,
                isin: s.isin,
                cusip: s.cusip,
                sedol: s.sedol,
                ticker: s.ticker,
                inceptionDate: s.inceptionDate,

                isCurrent: s.entityId === primarySeries.entityId,
                seriesTypeName: s.seriesType?.name,
                seriesTypeFullName: s.seriesType?.fullName,
                seriesTypeShortName: s.seriesType?.shortName,
                seriesTypeDescription: s.seriesType?.description,
                seriesTypeSortOrder: s.seriesType?.sortOrder,

                // seriesCurrencyName: s.currency?.name,
                seriesCurrencyIso4217alpha: s.currency?.iso4217alpha,

                fundStatusName: s.fundStatus?.name,
                fundStatus: s.fundStatus,

                minimumInitialInvestment: s.minimumInitialInvestment,
                minimumSubsequentInvestment: s.minimumSubsequentInvestment,
                minimumPac: s.minimumPac,
                minimumSubsequentPAC: s.minimumSubsequentPAC,
                minimumInitialInvestmentRRSP: s.minimumInitialInvestmentRRSP,
                minimumSubsequentInvestmentRRSP: s.minimumSubsequentInvestmentRRSP,
                minimumInvestment: s.minimumInvestment,
                minimumInvestmentPAC: s.minimumInvestmentPAC,

                registeredPlanEligibility: primarySeries.registeredPlanEligibility,
                dividendReinvestmentPlanEligibility:
                    primarySeries.dividendReinvestmentPlanEligibility,
                rebalancingFrequency: s.rebalancingFrequency,
                rebalancingThreshold: s.rebalancingThreshold,

                aum: s.aum,
                aumDate: s.aumDate,

                fundCodes: (s.fundCodes || [])?.map((x): viewModelTypes.FundSeriesFundCode => {
                    // if sales option does not have product and product group info and fund does,
                    // use the fund values.
                    const productGroup = x.productGroup || fund.productGroup;
                    const product = x.product || fund.product;

                    return {
                        entityId: x.entityId,
                        fundservCode: x.fundservCode,

                        ifastClass: x.ifastClass,
                        priceCode: x.priceCode,

                        seriesClassId: x.seriesClass?.entityId,
                        seriesClassName: x.seriesClass?.name,
                        seriesClassPerformanceSeries: x.seriesClass?.performanceSeries,

                        fundStatus: x.fundStatus,
                        fundStatusName: x.fundStatus?.name,
                        lineId: x.line?.entityId,
                        lineName: x.line?.name,

                        newSLICodes: x.newSLICodes,
                        minimumInitialInvestment: x.minimumInitialInvestment,
                        fundFeeRate: x.fundFeeRate,
                        scBackEndComm: x.scBackEndComm,
                        scNoLoadComm: x.scNoLoadComm,

                        loadType: x.loadType?.entityId,
                        loadTypeCode: x.loadType?.code,
                        loadTypeName: x.loadType?.name,
                        loadTypeShortName: x.loadType?.shortName,

                        productGroup: productGroup,
                        productGroupEntityId: productGroup?.entityId,
                        productGroupName: productGroup?.name,
                        productGroupSortOrder: productGroup?.sortOrder,

                        product: product,
                        productEntityId: product?.entityId,
                        productName: product?.name,
                        productSortOrder: product?.sortOrder,

                        profileId: '',
                    };
                }),
                fees: (s.fees || [])[0] || null,
                performance: {
                    annualPerformance:
                        (((s.performance || [])[0] || {}).annualPerformance || [])[0] || null,
                    compoundPerformance:
                        (((s.performance || [])[0] || {}).compoundPerformance || [])[0] || null,
                    growth: ((s.performance || [])[0] || {})._performance_growth || null,
                    monthlyDistributions: ((s.performance || [])[0] || {}).monthlyDistributions,
                    monthlyDistributionBreakdowns: s.monthlyDistributionBreakdowns,
                },
            })
        );

    overview.benchmarks = [];

    if (fund.benchmark1) {
        overview.benchmarks.push({
            entityId: fund.benchmark1.entityId,
            name: fund.benchmark1.name,
            isBlendedBenchmark: fund.benchmark1.isBlendedBenchmark,
            prices: fund.benchmark1.prices,
        });
    }

    if (fund.benchmark2) {
        overview.benchmarks.push({
            entityId: fund.benchmark2.entityId,
            name: fund.benchmark2.name,
            isBlendedBenchmark: fund.benchmark2.isBlendedBenchmark,
        });
    }

    if (fund.benchmark3) {
        overview.benchmarks.push({
            entityId: fund.benchmark3.entityId,
            name: fund.benchmark3.name,
            isBlendedBenchmark: fund.benchmark3.isBlendedBenchmark,
        });
    }

    if (fund.underlyingFund) {
        overview.underlyingFund = {
            entityId: fund.underlyingFund.entityId,
        };

        if (
            fund.underlyingFund.annualPerformance &&
            fund.underlyingFund.annualPerformance.length > 0
        ) {
            overview.underlyingFund.annualPerformance = fund.underlyingFund.annualPerformance[0];
        }

        if (
            fund.underlyingFund.compoundPerformance &&
            fund.underlyingFund.compoundPerformance.length > 0
        ) {
            overview.underlyingFund.compoundPerformance =
                fund.underlyingFund.compoundPerformance[0];
        }

        if (fund.underlyingFund._performance_growth) {
            overview.underlyingFund.growth = fund.underlyingFund._performance_growth;
        }
    }

    if (primarySeries.index) {
        overview.seriesIndex = primarySeries.index;
    }

    overview.fundHasLipperAward = fund.lipperFundAwards === true;

    let holdings: viewModelTypes.HoldingsViewModel | undefined;

    if (Array.isArray(fund.portfolios) && fund.portfolios.length > 0) {
        holdings = transformPortfolioTypeToHoldingsViewModel(fund.portfolios[0], webProfile);
    }

    const historicalHoldings: viewModelTypes.HoldingsViewModel[] = [];

    if (Array.isArray(fund.historicalPortfolios) && fund.historicalPortfolios.length > 0) {
        for (let i = 0; i < fund.historicalPortfolios.length; i++) {
            const historicalPortfolio = fund.historicalPortfolios[i];
            historicalHoldings.push(
                transformPortfolioTypeToHoldingsViewModel(historicalPortfolio, webProfile)
            );
        }
    }

    let managers: viewModelTypes.FundManager[] = [];

    if (Array.isArray(fund.fundManagers) && fund.fundManagers.length > 0) {
        managers = fund.fundManagers.map((m) => {
            const manager = m.manager;
            let vmManager: viewModelTypes.Manager | undefined;

            if (manager) {
                // remove articles1, articles2, articles3, articles4
                const { articles1, articles2, articles3, articles4, ...managerRes } = manager;
                vmManager = managerRes;
            }

            return {
                ...m,
                manager: vmManager,
            };
        });
    }

    let performance: viewModelTypes.PerformanceViewModel | undefined;
    const annualPerformance = (primarySalesOption.annualPerformance || [])[0] || null;
    const compoundPerformance = (primarySalesOption.compoundPerformance || [])[0] || null;
    const annualDistributions = primarySalesOption.annualDistributions;
    const growth = primarySalesOption._performance_growth;
    const prices = primarySalesOption.prices;
    const annualDistributionBreakdowns = primarySeries.annualDistributionBreakdowns;
    const monthlyDistributionBreakdowns = primarySeries.monthlyDistributionBreakdowns;

    if (primarySalesOption._performance_dailyPerformance) {
        if (!performance) {
            performance = {};
        }

        performance.dailyPerformance = primarySalesOption._performance_dailyPerformance;
    }

    if (annualDistributionBreakdowns) {
        if (!performance) {
            performance = {};
        }

        performance.annualDistributionBreakdowns = annualDistributionBreakdowns;
    }

    if (monthlyDistributionBreakdowns) {
        if (!performance) {
            performance = {};
        }

        performance.monthlyDistributionBreakdowns = monthlyDistributionBreakdowns;
    }

    if (annualDistributions) {
        if (!performance) {
            performance = {};
        }

        performance.annualDistributions = annualDistributions;
    }

    if (compoundPerformance && compoundPerformance.p1yr) {
        if (!performance) {
            performance = {};
        }

        performance.effectiveDate = compoundPerformance.effectiveDate;

        if (annualPerformance) {
            performance.annualPerformance = [
                {
                    type: 'SERIES',
                    isCurrent: true,
                    entityId: primarySeries.entityId,
                    name: `${fund.fullName} - ${primarySeries.seriesType?.name}`,
                    values: annualPerformance,
                },
            ];

            if (
                fund.benchmark1?.annualPerformance &&
                Array.isArray(fund.benchmark1.annualPerformance) &&
                fund.benchmark1.annualPerformance.length > 0
            ) {
                performance.annualPerformance.push({
                    type: 'BENCHMARK',
                    isCurrent: false,
                    entityId: fund.benchmark1.entityId,
                    name: fund.benchmark1.name,
                    values: fund.benchmark1.annualPerformance[0],
                });
            }
        }

        if (compoundPerformance) {
            performance.compoundPerformance = [
                {
                    type: 'SERIES',
                    isCurrent: true,
                    entityId: primarySeries.entityId,
                    name: `${fund.fullName} - ${primarySeries.seriesType?.name}`,
                    values: compoundPerformance,
                },
            ];

            if (
                fund.benchmark1?.compoundPerformance &&
                Array.isArray(fund.benchmark1.compoundPerformance) &&
                fund.benchmark1.compoundPerformance.length > 0
            ) {
                performance.compoundPerformance.push({
                    type: 'BENCHMARK',
                    isCurrent: false,
                    entityId: fund.benchmark1.entityId,
                    name: fund.benchmark1.name,
                    values: fund.benchmark1.compoundPerformance[0],
                });
            }
        }

        if (growth) {
            performance.growth = [
                {
                    type: 'SERIES',
                    isCurrent: true,
                    entityId: primarySeries.entityId,
                    name: `${fund.fullName} - ${primarySeries.seriesType?.name}`,
                    values: growth,
                },
            ];
        }

        const riskMetrics = (primarySalesOption.riskMetrics || [])[0] || null;

        if (riskMetrics && riskMetrics.effectiveDate) {
            const periods = ['3yr', '5yr', '10yr', 'si'];
            const fundName = `${fund.fullName} - ${primarySeries.seriesType?.name}`;
            const benchmarkName = fund.benchmark1?.name;

            performance.riskMetric = {
                effectiveDate: `${riskMetrics.effectiveDate}`,
                fund: fundName,
                benchmark: benchmarkName,
                periods: [],
            };

            for (const p of periods) {
                if (!riskMetrics[`r${p}StartDate`]) {
                    continue;
                }

                performance.riskMetric.periods.push({
                    period: p,
                    periodStartDate: riskMetrics[`r${p}StartDate`] as string,
                    values: [
                        {
                            key: 'annualizedSTDev',
                            fund: riskMetrics[`r${p}AnnualizedSTDev`],
                            benchmark: riskMetrics[`r${p}B1AnnualizedStdev`],
                        },
                        {
                            key: 'annualizedReturn',
                            fund: riskMetrics[`r${p}AnnualizedReturn`],
                            benchmark: riskMetrics[`r${p}B1AnnualizedReturn`],
                        },
                        {
                            key: 'maxDrawdown',
                            fund: riskMetrics[`r${p}MaxDrawdown`],
                            benchmark: riskMetrics[`r${p}B1MaxDrawdown`],
                        },
                        {
                            key: 'maxDrawdownDate',
                            fund: riskMetrics[`r${p}MaxDrawdownDate`],
                            benchmark: riskMetrics[`r${p}B1MaxDrawdownDate`],
                        },
                        {
                            key: 'peakValue',
                            fund: riskMetrics[`r${p}PeakValue`],
                            benchmark: riskMetrics[`r${p}B1PeakValue`],
                        },
                        {
                            key: 'peakValueDate',
                            fund: riskMetrics[`r${p}PeakValueDate`],
                            benchmark: riskMetrics[`r${p}B1PeakValueDate`],
                        },
                        {
                            key: 'b1Stdev',
                            fund: riskMetrics[`r${p}B1Stdev`],
                            benchmark: null,
                        },
                        {
                            key: 'b1SharpRatio',
                            fund: riskMetrics[`r${p}B1SharpRatio`],
                            benchmark: riskMetrics[`r${p}B1SharpRatioOfBenchmark`],
                        },
                        {
                            key: 'b1InformationRatio',
                            fund: riskMetrics[`r${p}B1InformationRatio`],
                            benchmark: null,
                        },
                        {
                            key: 'b1Beta',
                            fund: riskMetrics[`r${p}B1Beta`],
                            benchmark: null,
                        },
                        {
                            key: 'b1RSquared',
                            fund: riskMetrics[`r${p}B1RSquared`],
                            benchmark: null,
                        },
                        {
                            key: 'b1Alpha',
                            fund: riskMetrics[`r${p}B1Alpha`],
                            benchmark: null,
                        },
                        {
                            key: 'b1UpsideCapture',
                            fund: riskMetrics[`r${p}B1UpsideCapture`],
                            benchmark: null,
                        },
                        {
                            key: 'b1DownsideCapture',
                            fund: riskMetrics[`r${p}B1DownsideCapture`],
                            benchmark: null,
                        },
                        {
                            key: 'targetRateAnnualizedSortinoRatio',
                            fund: riskMetrics[`r${p}TargetRateAnnualizedSortinoRatio`],
                            benchmark: riskMetrics[`r${p}B1TargetRateAnnualizedSortinoRatio`],
                        },
                        {
                            key: 'annualizedTrackingError',
                            fund: riskMetrics[`r${p}AnnualizedTrackingError`],
                            benchmark: null,
                        },
                        {
                            key: 'stdev',
                            fund: riskMetrics[`r${p}Stdev`],
                            benchmark: null,
                        },
                        {
                            key: 'b1SharpRatioOfBenchmark',
                            fund: riskMetrics[`r${p}B1SharpRatioOfBenchmark`],
                            benchmark: null,
                        },
                        {
                            key: 'b1AnnualizedReturn',
                            fund: riskMetrics[`r${p}B1AnnualizedReturn`],
                            benchmark: null,
                        },
                        {
                            key: 'b2AnnualizedReturn',
                            fund: riskMetrics[`r${p}B2AnnualizedReturn`],
                            benchmark: null,
                        },
                        {
                            key: 'b1MaxDrawdown',
                            fund: riskMetrics[`r${p}B1MaxDrawdown`],
                            benchmark: null,
                        },
                        {
                            key: 'b2MaxDrawdown',
                            fund: riskMetrics[`r${p}B2MaxDrawdown`],
                            benchmark: null,
                        },
                        {
                            key: 'b2Stdev',
                            fund: riskMetrics[`r${p}B2Stdev`],
                            benchmark: null,
                        },
                        {
                            key: 'b2SharpRatioOfBenchmark',
                            fund: riskMetrics[`r${p}B2SharpRatioOfBenchmark`],
                            benchmark: null,
                        },
                    ],
                });
            }
        }
    }

    if (prices) {
        if (!performance) {
            performance = {};
        }

        performance.prices = [
            {
                type: 'SERIES',
                isCurrent: true,
                entityId: primarySeries.entityId,
                name: `${fund.fullName} - ${primarySeries.seriesType?.name}`,
                values: prices,
            },
        ];
    }

    const getLastMonthEndEffectiveDate = () => {
        const date = new Date();
        const y = date.getFullYear();
        const m = date.getMonth();
        const today = new Date().toISOString().substring(0, 10);
        const lastMonthEnd = new Date(y, m, 0).toISOString().substring(0, 10);
        const thisMonthEnd = new Date(y, m + 1, 0).toISOString().substring(0, 10);

        if (today >= thisMonthEnd) {
            return thisMonthEnd;
        } else {
            return lastMonthEnd;
        }
    };

    const latestPriceRecord = primarySalesOption.latestPriceRecord;

    if (Array.isArray(latestPriceRecord) && latestPriceRecord.length >= 1) {
        if (!performance) {
            performance = {};
        }

        latestPriceRecord.sort((a, b) => {
            if ((a.effectiveDate || '') > (b.effectiveDate || '')) {
                return -1;
            }

            if ((a.effectiveDate || '') < (b.effectiveDate || '')) {
                return 1;
            }

            return 0;
        });

        performance.price = latestPriceRecord[0];

        const lastMonthEndDate = getLastMonthEndEffectiveDate();
        const monthEndPriceRecord = latestPriceRecord
            .filter((p) => (p.effectiveDate || '') <= (lastMonthEndDate || ''))
            .find(() => true);

        performance.monthEndPrice = monthEndPriceRecord;
    }

    const managementFeeReductions = primarySeries.managementFeeReductions || null;
    const morningStarSeriesRatings = (primarySeries.morningStarSeriesRatings || [])[0] || null;

    const fundFundamentalMetrics =
        Array.isArray(fund.fundamentalMetrics) && fund.fundamentalMetrics.length > 0
            ? fund.fundamentalMetrics[0]
            : undefined;
    const benchmark1FundamentalMetrics =
        fund.benchmark1 &&
        Array.isArray(fund.benchmark1?.fundamentalMetrics) &&
        fund.benchmark1?.fundamentalMetrics?.length > 0
            ? fund.benchmark1.fundamentalMetrics[0]
            : undefined;

    const benchmark2FundamentalMetrics =
        fund.benchmark2 &&
        Array.isArray(fund.benchmark2?.fundamentalMetrics) &&
        fund.benchmark2?.fundamentalMetrics?.length > 0
            ? fund.benchmark2.fundamentalMetrics[0]
            : undefined;

    return {
        redirectUrl301:
            webProfile.documents?.find((x) => (x.tags || '').indexOf('301redirecturl') > -1)?.url ||
            '',
        redirectUrl302:
            webProfile.documents?.find((x) => (x.tags || '').indexOf('302redirecturl') > -1)?.url ||
            '',
        entityId: webProfile.entityId,
        locale,
        isInactive: webProfile.isInactive,
        title: webProfile.title,
        description: webProfile.description,
        pdfFilename: webProfile.pdfFilename,

        showEquityCharacteristic: webProfile.equityCharacteristic,
        showFixedIncomeCharacteristic: webProfile.fixedIncomeCharacteristic,

        overview,
        holdings,
        historicalHoldings,
        performance,
        managers,
        annotations,
        fundamentalMetrics: fundFundamentalMetrics,
        benchmark1FundamentalMetrics: benchmark1FundamentalMetrics,
        benchmark2FundamentalMetrics: benchmark2FundamentalMetrics,
        managementFeeReductions,
        morningStarSeriesRatings,
        relatedDocuments: webProfile.documents,
        articles,
        allProfiles: allProfiles,
    };
};

export const fetchWebProfileData = async (
    dataSource: string,
    variables: Record<string, string>,
    gqlClient: coreGraphqlTypes.IGraphqlClient
) => {
    const { query, transform } = buildQuery();

    const extensions: Record<string, unknown> = {};

    if (transform) {
        extensions.transform = transform;
    }

    const params = {
        query: query || '',
        extensions,
        variables,
    };

    const defaultLocale = 'en-US';
    let locale = params.variables.locale;

    if (typeof params.variables.locale !== 'string' || params.variables.locale.length < 1) {
        locale = params.variables.locale = defaultLocale;
    }

    const result = await gqlClient.query<types.WebProfileResponse>(dataSource, params);
    return transformData(result || { data: {} }, locale);
};
